(ns nl.mediquest.questionnaire-frontend.views
  (:require
   [nl.mediquest.questionnaire-frontend.component.i18n :as i18n]
   [nl.mediquest.questionnaire-frontend.component.notifications
    :refer [render-notifications]]
   [nl.mediquest.questionnaire-frontend.component.privacy :as privacy]
   [nl.mediquest.questionnaire-frontend.component.questionnaire-menu :as questionnaire-menu]
   [nl.mediquest.questionnaire-frontend.component.report :as report]
   [nl.mediquest.questionnaire-frontend.component.survey :as survey]
   [nl.mediquest.questionnaire-frontend.config :as config]
   [nl.mediquest.questionnaire-frontend.events :as events]
   [nl.mediquest.questionnaire-frontend.subs :as subs]
   [nl.mediquest.questionnaire-frontend.util :refer [navigate-to-questionnaire
                                                     get-current-year
                                                     nothing-filled-in?
                                                     labeled-input-field]]
   [nl.mediquest.questionnaire-frontend.vitaliteitsscan :as vitaliteitsscan]
   [nl.mediquest.ui.core.body]
   [nl.mediquest.ui.core.card]
   [nl.mediquest.ui.core.footer]
   [nl.mediquest.ui.core.header]
   [nl.mediquest.ui.core.modal :as modal]
   [nl.mediquest.ui.core.page]
   [nl.mediquest.ui.core.section]
   [re-frame.core :as re-frame]
   [react]
   [reagent.core :as r]))

(defn- header []
  (let [logo-src (re-frame/subscribe [:questionnaire/header-logo-src])
        unsubscribe? (re-frame/subscribe [:questionnaire/unsubscribe?])
        show-language-dropdown? (re-frame/subscribe [:questionnaire/show-language-dropdown?])]
    [:div.h-16.border-b.border-gray-200.bg-white.z-10
     [:div.container.mx-auto.h-16.leading-none
      [:div.h-full.items-center.flex.justify-between.px-4.sm:px-0.content-center
       [:div.space-x-4.flex
        [:img#header-img.h-8 {:src @logo-src}]
        [:div.border-l.border-gray-200.md:mx-10.sm:block.hidden]
        [:span.text-2xl.text-primary.leading-tight.sm:block.hidden.font-semibold
         (if @unsubscribe?
           [i18n/text :landing-page/unsubscribe-page]
           [i18n/text :landing-page/questionnaire])]]
       (when @show-language-dropdown?
         [i18n/language-dropdown])]]]))

(defn- footer []
  [:div.min-h-16.py-5.bg-footer.text-white.flex-shrink-0
   [:div.container.mx-auto
    [:div.h-full.flex-wrap.flex.justify-between.content-center.text-center
     [:div.w-full.md:w-auto
      [:a {:href "home.mediquest.nl"
           :target :_blank}
       [:img#footer-img.inline-block.h-8.ml-4 {:src "/images/mq-logo.png"}]]]
     [:div.text-center.w-full.md:w-auto.pt-1
      [:a.block.md:inline-block.text-white.no-underline
       {:href "https://home.mediquest.nl/privacy-statement"
        :target :_blank}
       "Privacy Statement"]
      [:div.block.md:inline-block.px-4 "© " (get-current-year)
       " Mediquest B.V. All rights reserved."]]]]])

(defn contact-bij-vragen []
  (let [contact-bij-vragen-hiccup
        @(re-frame/subscribe [:questionnaire/contact-bij-vragen-hiccup])]
    [:<>
     [:p.mt-8.text-xl [i18n/text :landing-page/questions]]
     (if contact-bij-vragen-hiccup
       contact-bij-vragen-hiccup
       [:p [i18n/text :landing-page/contact]
        [:a {:href "mailto:info@mediquest.nl"
             :target :_blank}
         "info@mediquest.nl"]
        "."])]))

(defn deelname-anoniem []
  (let [deelname-anoniem-hiccup
        @(re-frame/subscribe [:questionnaire/deelname-anoniem-hiccup])]
    [:<>
     [:p.mt-4.title.text-xl [i18n/text :landing-page/anonymous]]
     (if deelname-anoniem-hiccup
       deelname-anoniem-hiccup
       [:p [i18n/text :landing-page/mediquest]
        [:a
         {:href "https://home.mediquest.nl/privacy-statement" :target :_blank}
         [i18n/text :landing-page/here]]
        " "
        [i18n/text :landing-page/full-statement]])]))

(defn vragenlijstcode-form []
  (let [state (r/atom {:questionnaire-deployment-id nil})]
    (fn []
      [:form
       {:on-submit
        (fn [e]
          (.preventDefault e)
          (navigate-to-questionnaire @state))}
       [:div.md:flex.items-center
        [:div.pb-3.md:pb-0
         [labeled-input-field state :questionnaire-deployment-id
          (i18n/text :landing-page/code) true]]
        [:div.md:pl-3
         [:span.button.text-white.bg-primary
          {:on-click #(navigate-to-questionnaire @state)
           :disabled (nothing-filled-in? state)}
          [i18n/text :landing-page/retrieve]]]]])))

(defn questionnaire-panel []
  (when config/debug?
    (prn ::questionnaire-panel))
  (let [fetching? (re-frame/subscribe [:questionnaire/fetching?])
        survey-model (re-frame/subscribe [:questionnaire/survey-model])
        surveyjs-config (re-frame/subscribe [:questionnaire/survey])]
    (if (and @survey-model @surveyjs-config)
      [survey/panel @survey-model @surveyjs-config]
      [:div.h-40.p-16
       (when @fetching?
         "...")])))

(defn unavailable-error-key
  [{:questionnaire/keys [number-filled-out
                         max-per-questionnaire-deployment-id
                         expired?]
    :as _deploy_status}]
  (cond
    (= 1 number-filled-out max-per-questionnaire-deployment-id) :notifications/already-filled-out
    (>= number-filled-out max-per-questionnaire-deployment-id) :unavailable/max-number-filled-out-reached
    expired? :notifications/expired))

(defn deployment-unavailable-details [_q-id _q-d-id deploy-status]
  (let [i18n-error-key (unavailable-error-key deploy-status)]
    [:p.mt-4.mb-4
     [i18n/text i18n-error-key]]))

(defn deployment-unavailable-panel []
  (let [fetching? (re-frame/subscribe [:questionnaire/fetching?])
        questionnaire-id (re-frame/subscribe [:questionnaire/questionnaire-id])
        questionnaire-deployment-id
        (re-frame/subscribe [:questionnaire/questionnaire-deployment-id])
        deploy-status (re-frame/subscribe [:questionnaire/deploy-status])]
    [:div.m-4
     [:p.text-xl [i18n/text :landing-page/unfortunately]]
     [:p.mt-4.mb-4 [i18n/text :landing-page/unavailable]
      [i18n/text :landing-page/unavailable-reason]]
     (if @deploy-status
       [deployment-unavailable-details
        @questionnaire-id
        @questionnaire-deployment-id
        @deploy-status]
       [:div.h-40.p-16
        (when @fetching?
          "...")])
     [contact-bij-vragen]]))

(defn deploy-status-details
  [questionnaire-id
   questionnaire-deployment-id
   {:questionnaire/keys [number-filled-out
                         max-per-questionnaire-deployment-id
                         expired?]
    :as _deploy-status}]
  [:<>
   (when (or number-filled-out expired?)
     [:p.mt-4.mb-4
      "De '" questionnaire-id "' vragenlijst is "
      (if expired?
        "verlopen "
        [:<> [:span.font-bold number-filled-out] " keer ingevuld "])
      "voor code " questionnaire-deployment-id "."])
   (if max-per-questionnaire-deployment-id
     (let [remaining (- max-per-questionnaire-deployment-id number-filled-out)]
       [:p "Van het maximaal aantal " max-per-questionnaire-deployment-id
        (if (= 1 remaining) " is " " zijn ") "er nog "
        [:span.font-bold remaining]
        " over."])
     [:p "Er is geen maximaal aantal geconfigureerd."])
   [contact-bij-vragen]])

(defn deploy-status-panel []
  (let [fetching? (re-frame/subscribe [:questionnaire/fetching?])
        questionnaire-id (re-frame/subscribe [:questionnaire/questionnaire-id])
        questionnaire-deployment-id
        (re-frame/subscribe [:questionnaire/questionnaire-deployment-id])
        deploy-status (re-frame/subscribe [:questionnaire/deploy-status])]
    [:div.m-4
     [:p.text-xl "Status vragenlijstcode "
                 @questionnaire-id "/" @questionnaire-deployment-id]
     (if @deploy-status
       [deploy-status-details @questionnaire-id
                              @questionnaire-deployment-id
                              @deploy-status]
       [:div.h-40.p-16
        (when @fetching?
          "...")])]))

(defn home-panel []
  [:div.m-4
   [:p.text-xl [i18n/text :landing-page/unfortunately]]
   [:p.mt-4.mb-4 [i18n/text :landing-page/unavailable]]
   [contact-bij-vragen]])

(defn landingspage-panel []
  (when config/debug?
    (prn ::landingspage-panel))
  (re-frame/dispatch [::events/fetch-survey-config])
  (let [questionnaire (re-frame/subscribe [:questionnaire/config])
        backend-error (re-frame/subscribe [:questionnaire/backend.error])]
    (fn []
      [:div.m-4
       (if @backend-error
         [:p.text-xl
          [:span.bold
           [i18n/text :landing-page/error]]]
         (if config/vitaliteitsscan?
           [vitaliteitsscan/landing-page]
           [:<>
            [:p.text-xl
             (or
              (:questionnaire/welcome-text @questionnaire)
              (str (i18n/text :landing-page/welcome)
                   (:questionnaire/title @questionnaire)))]
            [:p.mt-4.mb-4 [i18n/text :landing-page/at-code-below]]
            [vragenlijstcode-form]
            (when-let [{:questionnaire/keys
                        [suppress-landing-anonymous-paragraph?
                         suppress-landing-contact-paragraph?]}
                       @questionnaire]
              [:<>
               (when-not suppress-landing-anonymous-paragraph?
                 [deelname-anoniem])
               (when-not suppress-landing-contact-paragraph?
                 [contact-bij-vragen])])]))])))

(defn thank-you-panel [questionnaire-id]
  [:div.m-4
   [:p.text-xl [i18n/text :thank-you-page/message] questionnaire-id]])

(defn result-panel []
  (let [questionnaire-id (re-frame/subscribe [:questionnaire/questionnaire-id])]
    (if-let [report-data @(re-frame/subscribe [::subs/report-data])]
      [report/panel @questionnaire-id report-data]
      [thank-you-panel @questionnaire-id])))

(def ^:private rtl-lang?
  #{:ar})

(defn- with-default-header-and-footer [body]
  [:<>
   [modal/view]
   [render-notifications]
   [:div.flex.flex-col.min-h-screen.bg-gray-100
    [header]
    [:div.flex-grow.relative
     (let [selected-locale @(re-frame/subscribe [::subs/selected-locale])]
       [:div.container.mx-auto.py-8
        {:dir (if (rtl-lang? selected-locale) "rtl" "ltr")}
        [body]])]
    [footer]]])

(defn with-header-and-footer [body]
  ;; will we delegate page layout to mq-ui or not?
  (if config/vitaliteitsscan?
    [vitaliteitsscan/with-header-and-footer body]
    [with-default-header-and-footer body]))

(defn show-panel [panel-name]
  (when config/debug?
    (prn ::show-panel panel-name))
  (condp = panel-name
    :home-panel [with-header-and-footer home-panel]
    :landingspage-panel [with-header-and-footer landingspage-panel]
    :privacy-panel [with-header-and-footer privacy/privacy-panel]
    :result-panel [result-panel]
    :questionnaire-panel [with-header-and-footer questionnaire-panel]
    :menu-panel [with-header-and-footer questionnaire-menu/view]
    :deployment-unavailable-panel [with-header-and-footer deployment-unavailable-panel]
    :deploy-status-panel [with-header-and-footer deploy-status-panel]
    [:div]))

(defn main-panel []
  (let [active-panel (re-frame/subscribe [::subs/active-panel])]
    [show-panel @active-panel]))
